

export type Platform = "ios" | "android";
export type Method   = "qr" | "manual";
export type Section  = "install" | "activate";

export interface Step {
  title: string;
  label: string;
  img:   string;
  desc:  string;
  tip:   string;
}

export const STEPS: Record<Platform, Record<Section | "install", Record<Method, Step[]> | Step[]>> = {
  ios: {
    install: {
      qr: [
        {
          title: "Open Settings",
          label: "Settings › Mobile",
          img: "1.webp",
          desc: "Open the Settings app on your iPhone and tap Mobile (or Cellular). This is where all SIM management lives.",
          tip: "Connect to Wi-Fi first — the eSIM profile is downloaded over the internet.",
        },
        {
          title: "Tap Add eSIM",
          label: "Mobile › Add eSIM",
          img: "2.webp",
          desc: "Scroll to the bottom of the Mobile screen and tap Add eSIM to open the eSIM setup wizard.",
          tip: "If Add eSIM isn't visible, your device may not support eSIM or it's carrier-locked.",
        },
        {
          title: "Choose Use QR Code",
          label: "Set Up eSIM",
          img: "3.webp",
          desc: "On the Set Up eSIM screen tap Use QR Code. Your camera opens ready to scan the code your activation QR code.",
          tip: "keep your email open on another device to access the QR code.",
        },
        {
          title: "Scan the QR code",
          label: "QR scanner",
          img: "4.webp",
          desc: "Hold your iPhone steady over the Vell QR code. It's recognised automatically — no button press needed.",
          tip: "If scanning fails, tap Enter Details Manually at the bottom of the screen.",
        },
        {
          title: "(Optional) Enter activation code",
          label: "Enter Details Manually",
          img: "6.webp",
          desc: "If QR isn’t available, manually input the SM-DP+ address and activation code. These are in your Vell order confirmation email. Tap Next when done.",
          tip: "It's helpful to copy-paste — the code is long and case-sensitive.",
        },
        {
          title: "Tap on the new eSIM entry",
          label: "Mobile Plan Label",
          img: "7.webp",
          desc: "Tap on the new eSIM entry that appears after scanning or manual entry. Then tap Mobile Plan Label to name your eSIM.",
          tip: "A clear label like 'VELL eSIM' makes it easy to find later in Settings.",
        },
        {
          title: "Tap Edit custom label",
          label: "Edit custom label",
          img: "9.webp",
          desc: "Choose a preset or tap Edit custom label to type your own.",
          tip: "A clear custom label like 'VELL eSIM' makes it easy to find later in Settings.",
        },
        {
          title: "Enter a custom label",
          label: "Edit custom label",
          img: "10.webp",
          desc: "Type a custom label for your eSIM, for example 'VELL eSIM' or 'VELL Japan'. Tap Done to save.",
          tip: "A clear custom label like 'VELL eSIM' makes it easy to find later in Settings.",
        },
        {
          title: "Done! Your eSIM is ready to activate",
          label: "Edit custom label",
          img: "11.webp",
          desc: "Please proceed to the activation steps to turn on your eSIM and connect to the network.",
          tip: "Your eSIM is installed but not active yet — you need to enable it in the next steps.",
        },
      ],
    } as Record<Method, Step[]>,
    activate: [
      {
        title: "Find your eSIM in Settings",
        label: "Settings › Mobile › SIMs",
        img: "11.webp",
        desc: "Go to Settings › Mobile. Scroll to the SIMs section and find VELL eSIM (or the name you gave it). It may show 'Off' until you enable it.",
        tip: "Showing as Off is normal — you'll turn it on in the next step.",
      },
      {
        title: "Open the eSIM detail",
        label: "Tap VELL eSIM row",
        img: "12.webp",
        desc: "Tap on the VELL eSIM row to open its detail screen, where you'll see the Mobile Plan Label and Turn on this Line toggle.",
        tip: "If it shows Off next to the name, you'll need to enable the toggle.",
      },
      {
        title: "Turn on this line",
        label: "Enable the toggle",
        img: "13.webp",
        desc: "Tap Turn on this Line to switch it on (green). Your iPhone will connect to the Vell network within a few seconds.",
        tip: "Watch the signal bar in the status bar — it'll change once the line is live.",
      },
      {
        title: "Set VELL as your data SIM",
        label: "Mobile Data › VELL eSIM",
        img: "14.webp",
        desc: "Back on the Mobile screen, tap Mobile Data and select VELL eSIM. A blue tick confirms it's your active data line.",
        tip: "Keep your primary SIM for calls and texts — use Vell eSIM for data only while travelling.",
      },
      {
        title: "Done! Your eSIM is ready to use",
        label: "Mobile Data › VELL eSIM",
        img: "15.webp",
        desc: "Your VELL eSIM is now active and set as your data SIM. Open a browser or map app to confirm you have connectivity.",
        tip: "If you don't connect right away, toggle Aeroplane mode on then off to refresh the connection.",
      },
      {
        title: "Turn on Data Roaming when you arrive",
        label: "Mobile › VELL eSIM › Data Roaming",
        img: "page16.webp",
        desc: "Once you land, go to Settings › Mobile › VELL eSIM and toggle Data Roaming on. This lets your Vell eSIM connect to local networks abroad.",
        tip: "Vell data is pre-paid — turning on roaming won't add surprise charges to your Vell plan.",
      },
    ] as Step[],
  },

  android: {
    install: {
      qr: [
        {
          title: "Open Connections",
          label: "Settings › Connections",
          img: "16.webp",
          desc: "Open Settings and tap Connections (Samsung) or Network & internet (stock Android). Then tap SIM manager.",
          tip: "On non-Samsung devices look for SIMs or eSIM directly in Network & internet.",
        },
        {
          title: "Tap Add eSIM",
          label: "SIM manager › Add eSIM",
          img: "17.webp",
          desc: "Scroll down and tap Add eSIM. Android will show you the options for adding a new eSIM profile.",
          tip: "Ignore any existing eSIM entries — those are previously installed profiles.",
        },
        {
          title: "Choose Scan QR code",
          label: "Choose how to add",
          img: "18.webp",
          desc: "On the 'Choose how to add your eSIM' screen, tap Scan QR code. Your camera will open.",
          tip: "If you saved the Vell QR code as an image, use the gallery icon at the bottom-right instead.",
        },
        {
          title: "Scan or pick from gallery",
          label: "QR scanner",
          img: "19.webp",
          desc: "Point your camera at the Vell QR code from your email, or tap the gallery icon to select a saved image of the code.",
          tip: "Make sure the QR code fills the frame — move closer if the camera doesn't register it.",
        },
        {
          title: "(Optional) Select the QR image",
          label: "Gallery picker",
          img: "20.webp",
          desc: "If you tapped the gallery icon, select the Vell QR code image. Android reads it automatically and begins the download.",
          tip: "Useful if you already screenshotted or downloaded the QR code from your email.",
        },
        {
          title: "(Optional) Tap manual entry at the bottom",
          label: "QR scanner screen",
          img: "23.webp",
          desc: "If scanning fails, tap Enter activation code at the bottom of the screen to switch to manual entry.",
          tip: "You must pass through the QR screen — the manual link is at the bottom of the scanner.",
        },
        {
          title: "Name your eSIM",
          label: "Edit SIM names",
          img: "24.webp",
          desc: "Tap the new eSIM entry, then tap the pencil icon to rename it. Type 'VELL eSIM' or your destination name.",
          tip: "A descriptive name helps you switch SIMs confidently without confusion.",
        },
        {
          title: "Confirm the SIM name",
          label: "SIM name dialog",
          img: "25.webp",
          desc: "Type your preferred name — e.g. 'VELL Japan' — choose an icon and tap Done to save.",
          tip: "Pick the travel suitcase icon to visually distinguish Vell from your primary SIM.",
        },
        {
          title: "Set preferred data SIM",
          label: "Preferred SIM for data",
          img: "26.webp",
          desc: "On the Preferred SIM for mobile data screen, tap your VELL eSIM. A highlight confirms the selection. Tap Done.",
          tip: "Keep SIM 1 for calls — use Vell eSIM for data only while roaming.",
        },
      ],
    } as Record<Method, Step[]>,
    activate: [
      {
        title: "Open SIM manager",
        label: "Settings › Connections › SIM manager",
        img: "26.webp",
        desc: "Go to Settings › Connections › SIM manager. Find your VELL eSIM in the eSIMs section and confirm the toggle is ON (blue).",
        tip: "If the toggle is grey, tap it to enable the eSIM profile before continuing.",
      },
      {
        title: "Tap Mobile data",
        label: "Preferred SIMs › Mobile data",
        img: "27.webp",
        desc: "Scroll to Preferred SIMs and tap Mobile data. A dropdown shows your available SIMs.",
        tip: "Leave Calls and Messages on SIM 1 — only switch Mobile data to Vell.",
      },
      {
        title: "Select VELL as data SIM",
        label: "Mobile data dropdown",
        img: "28.webp",
        desc: "Tap your VELL eSIM name in the dropdown — a checkmark confirms it's now your active data line.",
        tip: "You can switch back to your primary SIM at any time from the same menu.",
      },
      {
        title: "Open Mobile networks",
        label: "Connections › Mobile networks",
        img: "29.webp",
        desc: "Go back to Connections and tap Mobile networks. This is where you enable data roaming for your Vell eSIM.",
        tip: "Make sure VELL is selected as the active data SIM before enabling roaming.",
      },
      {
        title: "Enable Data roaming",
        label: "Data roaming toggle",
        img: "30.webp",
        desc: "Tap the Data roaming toggle to turn it ON. Your Vell eSIM will now connect to local networks abroad.",
        tip: "Vell data is pre-paid — you won't incur extra roaming charges on your Vell plan.",
      },
      {
        title: "You're connected",
        label: "Roaming active ✓",
        img: "31.webp",
        desc: "Data roaming is on and VELL eSIM is your data SIM. Open a browser tab to confirm you're online.",
        tip: "If you don't connect right away, toggle Aeroplane mode on then off to refresh.",
      },
    ] as Step[],
  },
};
